import { Button, Select, Space, Spin, Switch, Typography } from "@arco-design/web-react";
import { useCallback, useMemo, useRef, useState, useEffect } from "react";

import {
  type ConfirmationEntry,
  type ConfirmationEntryResourceType,
  type ConfirmationItineraryDay,
} from "../service/confirmationEntryParser";
import {
  filterFulfillmentSupplierOptionsForResource,
  fulfillmentSupplierEmptyMessage,
  isFulfillmentSupplierCompatibleWithResource,
} from "../service";
import { toFulfillmentLinePatch } from "../service/fulfillmentOrderQuickEntryAdapter";
import type {
  FulfillmentOrderLine,
  FulfillmentResourceMentionCandidate,
  FulfillmentResourceMentionLevel,
  FulfillmentResourceMentionType,
  FulfillmentResourceTransportMode,
  FulfillmentSupplierOption,
  FulfillmentTransportPoint,
  FulfillmentTransportResourceCandidate,
} from "../types";
import {
  FulfillmentOrderQuickEntry,
  FulfillmentStayCandidateOption,
  FulfillmentSupplierOption as FulfillmentSupplierOptionView,
} from "./FulfillmentOrderQuickEntry";

/** 费用调整复用确认件新增录入时选中的资源与结算主体。 */
export type FulfillmentOrderQuickEntrySubjects = {
  resourceType: ConfirmationEntryResourceType;
  staySpu: FulfillmentResourceMentionCandidate | null;
  fulfillmentSupplier: FulfillmentSupplierOption | null;
  settlementSupplier: FulfillmentSupplierOption | null;
  sameAsSettlement: boolean;
};

/** 把正式录入结果与共享主体选择合成为确认件行补丁。 */
export function toFulfillmentOrderQuickEntryLinePatches({
  entries,
  subjects,
  workspaceStatus,
}: {
  entries: readonly ConfirmationEntry[];
  subjects: FulfillmentOrderQuickEntrySubjects;
  workspaceStatus: string;
}): Partial<FulfillmentOrderLine>[] {
  const settlementSupplier = workspaceStatus !== "draft"
    ? subjects.settlementSupplier
    : subjects.sameAsSettlement && subjects.fulfillmentSupplier?.canBeSettlementSubject
      ? subjects.fulfillmentSupplier
      : null;
  return entries.map((entry) => ({
    ...toFulfillmentLinePatch(entry),
    ...(subjects.resourceType === "住宿"
      ? {
          fulfillmentSupplierId: null,
          fulfillmentSupplierName: null,
          settlementSupplierId: settlementSupplier?.id ?? null,
          settlementSupplierName: settlementSupplier?.name ?? null,
        }
      : {
          fulfillmentSupplierId: subjects.fulfillmentSupplier?.id ?? null,
          fulfillmentSupplierName: subjects.fulfillmentSupplier?.name ?? null,
          settlementSupplierId: settlementSupplier?.id ?? null,
          settlementSupplierName: settlementSupplier?.name ?? null,
        }),
  }));
}

type Props = {
  disabled?: boolean;
  onPendingTextChange?: (pending: boolean) => void;
  requireSettlementSupplier?: boolean;
  workspaceStatus: string;
  itineraryDays: readonly ConfirmationItineraryDay[];
  serviceProviderOptions: readonly FulfillmentSupplierOption[];
  settlementSupplierOptions: readonly FulfillmentSupplierOption[];
  /** 当前供应商候选实际对应的资源类型；用于等待异步候选切换完成后再校验旧选择。 */
  serviceProviderResourceType?: string | null;
  serviceProviderSearchKeyword?: string;
  serviceProviderLoading?: boolean;
  settlementSupplierLoading?: boolean;
  serviceProviderError?: unknown;
  settlementSupplierError?: unknown;
  serviceProviderErrorCode?: string | null;
  settlementSupplierErrorCode?: string | null;
  onRetryServiceProviders?: () => void;
  onRetrySettlementSuppliers?: () => void;
  onResourceTypeChange?: (resourceType: ConfirmationEntryResourceType) => void;
  onSearchServiceProviders?: (keyword: string) => void;
  onSearchSettlementSuppliers?: (keyword: string) => void;
  searchResourceCandidates?: (
    resourceType: FulfillmentResourceMentionType,
    level: FulfillmentResourceMentionLevel,
    keyword: string,
    spuId?: string | null,
    transportMode?: FulfillmentResourceTransportMode | null,
    fulfillmentSupplierId?: string | null,
  ) => Promise<FulfillmentResourceMentionCandidate[]>;
  searchTransportPoints?: (
    mode: FulfillmentResourceTransportMode,
    keyword: string,
  ) => Promise<FulfillmentTransportPoint[]>;
  searchTransportResourceCandidates?: (
    mode: FulfillmentResourceTransportMode,
    fromPointId: string,
    toPointId: string,
    specKeyword?: string | null,
  ) => Promise<FulfillmentTransportResourceCandidate[]>;
  onAdd: (
    entries: ConfirmationEntry[],
    subjects: FulfillmentOrderQuickEntrySubjects,
  ) => void | Promise<void>;
};

/**
 * 费用调整复用确认件新增录入的适配面板。
 *
 * 正式确认件主工作区保留原 `FulfillmentOrderQuickEntry` 装配；本面板仅供个人
 * 与费用调整复用六类录入，并在调整区域提供结算供应商选择。调用方只能决定
 * 识别结果如何落库，不能另写六类解析、住宿或履约主体交互。
 */
export function FulfillmentOrderQuickEntryPanel({
  disabled = false,
  onPendingTextChange,
  requireSettlementSupplier = false,
  workspaceStatus,
  itineraryDays,
  serviceProviderOptions,
  settlementSupplierOptions,
  serviceProviderResourceType,
  serviceProviderSearchKeyword = "",
  serviceProviderLoading = false,
  settlementSupplierLoading = false,
  serviceProviderError = null,
  settlementSupplierError = null,
  serviceProviderErrorCode = null,
  settlementSupplierErrorCode = null,
  onRetryServiceProviders,
  onRetrySettlementSuppliers,
  onResourceTypeChange,
  onSearchServiceProviders,
  onSearchSettlementSuppliers,
  searchResourceCandidates,
  searchTransportPoints,
  searchTransportResourceCandidates,
  onAdd,
}: Props) {
  const [resourceType, setResourceType] = useState<ConfirmationEntryResourceType>("住宿");
  const [fulfillmentSupplierId, setFulfillmentSupplierId] = useState<string>();
  const [sameAsSettlement, setSameAsSettlement] = useState(false);
  const [settlementSupplier, setSettlementSupplier] = useState<FulfillmentSupplierOption | null>(null);
  const [staySpuId, setStaySpuId] = useState<string>();
  const [stayOptions, setStayOptions] = useState<FulfillmentResourceMentionCandidate[]>([]);
  const [stayLoading, setStayLoading] = useState(false);
  const [stayError, setStayError] = useState(false);
  const stayGenerationRef = useRef(0);
  const stayCacheRef = useRef(new Map<string, FulfillmentResourceMentionCandidate>());
  const fulfillmentSupplierCacheRef = useRef(new Map<string, FulfillmentSupplierOption>());
  const pendingServiceProviderResourceTypeRef = useRef<ConfirmationEntryResourceType | null>(null);

  const compatibleServiceProviders = useMemo(
    () => filterFulfillmentSupplierOptionsForResource(serviceProviderOptions, resourceType),
    [resourceType, serviceProviderOptions],
  );
  compatibleServiceProviders.forEach((supplier) => {
    fulfillmentSupplierCacheRef.current.set(supplier.id, supplier);
  });
  const fulfillmentSupplier = fulfillmentSupplierId
    ? fulfillmentSupplierCacheRef.current.get(fulfillmentSupplierId) ?? null
    : null;
  const displayedServiceProviders = useMemo(
    () => fulfillmentSupplier
      && !compatibleServiceProviders.some((supplier) => supplier.id === fulfillmentSupplier.id)
      ? [fulfillmentSupplier, ...compatibleServiceProviders]
      : compatibleServiceProviders,
    [compatibleServiceProviders, fulfillmentSupplier],
  );
  const displayedSettlementSuppliers = useMemo(
    () => settlementSupplier
      && !settlementSupplierOptions.some((supplier) => supplier.id === settlementSupplier.id)
      ? [settlementSupplier, ...settlementSupplierOptions]
      : settlementSupplierOptions,
    [settlementSupplier, settlementSupplierOptions],
  );
  stayOptions.forEach((candidate) => {
    if (candidate.spuId) stayCacheRef.current.set(candidate.spuId, candidate);
  });
  const staySpu = staySpuId ? stayCacheRef.current.get(staySpuId) ?? null : null;
  const displayedStayOptions = useMemo(
    () => staySpu && !stayOptions.some((candidate) => candidate.spuId === staySpu.spuId)
      ? [staySpu, ...stayOptions]
      : stayOptions,
    [stayOptions, staySpu],
  );

  /** 搜索当前可用于正式确认件录入的住宿资源。 */
  const searchStaySpus = useCallback(async (keyword: string) => {
    if (!searchResourceCandidates) return;
    const generation = ++stayGenerationRef.current;
    setStayLoading(true);
    setStayError(false);
    try {
      const candidates = await searchResourceCandidates("住宿", "spu", keyword, null);
      if (generation !== stayGenerationRef.current) return;
      setStayOptions(candidates.filter((candidate) => (
        candidate.resourceType === "住宿"
        && candidate.level === "spu"
        && Boolean(candidate.spuId && candidate.spuName)
      )));
    } catch {
      if (generation !== stayGenerationRef.current) return;
      setStayOptions([]);
      setStayError(true);
    } finally {
      if (generation === stayGenerationRef.current) setStayLoading(false);
    }
  }, [searchResourceCandidates]);

  useEffect(() => {
    if (resourceType === "住宿") void searchStaySpus("");
  }, [resourceType, searchStaySpus]);

  useEffect(() => {
    if (
      pendingServiceProviderResourceTypeRef.current !== resourceType
      || (serviceProviderResourceType !== undefined && serviceProviderResourceType !== resourceType)
      || serviceProviderLoading
    ) return;
    pendingServiceProviderResourceTypeRef.current = null;
    if (
      !fulfillmentSupplierId
      || compatibleServiceProviders.some((supplier) => supplier.id === fulfillmentSupplierId)
    ) return;
    setFulfillmentSupplierId(undefined);
    setSameAsSettlement(false);
    if (settlementSupplier?.id === fulfillmentSupplierId) setSettlementSupplier(null);
  }, [
    compatibleServiceProviders,
    fulfillmentSupplierId,
    resourceType,
    serviceProviderResourceType,
    serviceProviderLoading,
    settlementSupplier?.id,
  ]);

  /** 切换六类正式录入时同步主体候选范围，并清理不兼容的旧履约主体。 */
  function changeResourceType(nextType: ConfirmationEntryResourceType) {
    setResourceType(nextType);
    pendingServiceProviderResourceTypeRef.current = nextType;
    onResourceTypeChange?.(nextType);
    if (fulfillmentSupplier
      && !isFulfillmentSupplierCompatibleWithResource(fulfillmentSupplier, nextType)) {
      pendingServiceProviderResourceTypeRef.current = null;
      setFulfillmentSupplierId(undefined);
      setSameAsSettlement(false);
      if (settlementSupplier?.id === fulfillmentSupplier.id) setSettlementSupplier(null);
    }
  }

  /** 在统一主体校验通过后把解析结果交给调用方选择实际持久化方式。 */
  async function addEntries(entries: ConfirmationEntry[]) {
    if (resourceType === "住宿" && !staySpu?.spuId) throw new Error("请先选择住宿酒店");
    if (resourceType !== "住宿" && !fulfillmentSupplier) throw new Error("请先选择履约供应商");
    if (requireSettlementSupplier && workspaceStatus !== "draft" && !settlementSupplier) throw new Error("请先选择结算供应商");
    await onAdd(entries, {
      resourceType,
      staySpu,
      fulfillmentSupplier,
      settlementSupplier,
      sameAsSettlement,
    });
  }

  const serviceProviderErrorNotice = (
    <span className="fulfillment-order-settlement-error">
      {serviceProviderErrorCode === "SUPPLIER_SCHEMA_NOT_READY"
        ? "供应商主数据未就绪"
        : "履约供应商加载失败"}
      {onRetryServiceProviders ? (
        <Button type="text" size="small" onClick={onRetryServiceProviders}>重试加载</Button>
      ) : null}
    </span>
  );

  return (
    <FulfillmentOrderQuickEntry
      onPendingTextChange={onPendingTextChange}
      itineraryDays={itineraryDays}
      disabled={disabled}
      selectedStaySpu={staySpu}
      searchResourceCandidates={searchResourceCandidates}
      searchTransportPoints={searchTransportPoints}
      searchTransportResourceCandidates={searchTransportResourceCandidates}
      subjectSlot={(
        <>
          {resourceType === "住宿" ? (
            <section className="fulfillment-order-entry-subject" aria-label="手工录入住宿酒店">
              <div className="fulfillment-order-entry-subject-label">
                <Typography.Text bold>住宿酒店</Typography.Text>
                <Typography.Text type="secondary">本次加入的房型统一归属所选住宿酒店</Typography.Text>
              </div>
              <Select
                className="fulfillment-order-stay-select"
                aria-label="选择住宿酒店"
                value={staySpu?.spuId ?? undefined}
                placeholder="按酒店名称、城市或地址搜索"
                notFoundContent={stayError ? "住宿酒店加载失败" : "暂无可用住宿酒店"}
                showSearch
                allowClear
                loading={stayLoading}
                disabled={disabled}
                options={displayedStayOptions.map((candidate) => ({
                  value: candidate.spuId!,
                  label: <FulfillmentStayCandidateOption candidate={candidate} />,
                }))}
                dropdownMenuClassName="fulfillment-order-stay-select-dropdown"
                renderFormat={(_, value) => {
                  const candidate = displayedStayOptions.find(
                    (item) => item.spuId === String(value),
                  );
                  return candidate ? <FulfillmentStayCandidateOption candidate={candidate} /> : String(value);
                }}
                filterOption={false}
                onSearch={(keyword) => { void searchStaySpus(keyword); }}
                onChange={(value) => setStaySpuId(value ? String(value) : undefined)}
              />
              {stayError ? (
                <div className="fulfillment-order-entry-subject-summary">
                  <span className="fulfillment-order-settlement-error">
                    住宿酒店加载失败
                    <Button type="text" size="small" onClick={() => { void searchStaySpus(""); }}>
                      重试加载
                    </Button>
                  </span>
                </div>
              ) : null}
            </section>
          ) : (
            <section className="fulfillment-order-entry-subject" aria-label="手工录入服务供应商">
              <div className="fulfillment-order-entry-subject-label">
                <Typography.Text bold>
                  {resourceType === "导游" ? "履约导游（个人）" : "服务供应商"}
                </Typography.Text>
                <Typography.Text type="secondary">
                  {resourceType === "导游"
                    ? "本次加入的全部费用将归属所选具体导游"
                    : "本次加入的全部明细将由该供应商实际提供服务"}
                </Typography.Text>
              </div>
              {serviceProviderLoading ? (
                <span className="fulfillment-order-settlement-loading">
                  <Spin size={16} /> 正在加载供应商主数据
                </span>
              ) : (
                <>
                  <Select
                    className="fulfillment-order-supplier-select"
                    aria-label="选择录入服务供应商"
                    value={fulfillmentSupplier?.id}
                    placeholder={displayedServiceProviders.length
                      ? "按名称、编号或拼音搜索服务供应商"
                      : fulfillmentSupplierEmptyMessage(
                        resourceType,
                        Boolean(serviceProviderSearchKeyword.trim()),
                      )}
                    notFoundContent={fulfillmentSupplierEmptyMessage(
                      resourceType,
                      Boolean(serviceProviderSearchKeyword.trim()),
                    )}
                    showSearch
                    allowClear
                    disabled={disabled}
                    options={displayedServiceProviders.map((supplier) => ({
                      value: supplier.id,
                  label: <FulfillmentSupplierOptionView supplier={supplier} />,
                }))}
                dropdownMenuClassName="fulfillment-order-supplier-select-dropdown"
                renderFormat={(_, value) => {
                  const supplier = displayedServiceProviders.find((item) => item.id === String(value));
                  return supplier ? <FulfillmentSupplierOptionView supplier={supplier} /> : String(value);
                }}
                    filterOption={false}
                    onSearch={onSearchServiceProviders}
                    onChange={(value) => {
                      setSameAsSettlement(false);
                      if (!value) {
                        setFulfillmentSupplierId(undefined);
                        return;
                      }
                      const selected = displayedServiceProviders.find(
                        (supplier) => supplier.id === String(value),
                      );
                      if (!selected || !isFulfillmentSupplierCompatibleWithResource(
                        selected,
                        resourceType,
                      )) {
                        setFulfillmentSupplierId(undefined);
                        return;
                      }
                      setFulfillmentSupplierId(selected.id);
                    }}
                  />
                  {displayedServiceProviders.length === 0 ? (
                    <Typography.Text type="secondary">
                      {fulfillmentSupplierEmptyMessage(
                        resourceType,
                        Boolean(serviceProviderSearchKeyword.trim()),
                      )}
                    </Typography.Text>
                  ) : null}
                  {serviceProviderError ? serviceProviderErrorNotice : null}
                </>
              )}
              <div className="fulfillment-order-entry-subject-summary fulfillment-order-entry-subject-summary--supplier">
                <Space>
                  <Switch
                    aria-label="与结算主体一致"
                    checked={sameAsSettlement && Boolean(fulfillmentSupplier?.canBeSettlementSubject)}
                    disabled={disabled || !fulfillmentSupplier?.canBeSettlementSubject}
                    onChange={(checked) => {
                      setSameAsSettlement(checked);
                      if (checked && fulfillmentSupplier?.canBeSettlementSubject) {
                        setSettlementSupplier(fulfillmentSupplier);
                      } else if (settlementSupplier?.id === fulfillmentSupplier?.id) {
                        setSettlementSupplier(null);
                      }
                    }}
                  />
                  <Typography.Text>与结算主体一致</Typography.Text>
                </Space>
              </div>
            </section>
          )}
        </>
      )}
      afterEntrySlot={workspaceStatus !== "draft" ? (
        <section className="fulfillment-order-entry-subject" aria-label="定稿后新增项目结算供应商">
          <div className="fulfillment-order-entry-subject-label">
            <Typography.Text bold>结算供应商</Typography.Text>
            <Typography.Text type="secondary">
              新增主项目将自动归入该供应商应付，并写入合同修改记录
            </Typography.Text>
          </div>
          <Select
            className="fulfillment-order-supplier-select"
            aria-label="选择定稿后结算供应商"
            value={settlementSupplier?.id}
            placeholder="按名称、编号或拼音搜索结算供应商"
            notFoundContent={settlementSupplierError ? "结算供应商加载失败" : "暂无可用结算供应商"}
            showSearch
            allowClear
            loading={settlementSupplierLoading}
            disabled={disabled}
            options={displayedSettlementSuppliers.map((supplier) => ({
              value: supplier.id,
              label: <FulfillmentSupplierOptionView supplier={supplier} />,
            }))}
            dropdownMenuClassName="fulfillment-order-supplier-select-dropdown"
            renderFormat={(_, value) => {
              const supplier = displayedSettlementSuppliers.find((item) => item.id === String(value));
              return supplier ? <FulfillmentSupplierOptionView supplier={supplier} /> : String(value);
            }}
            filterOption={false}
            onSearch={onSearchSettlementSuppliers}
            onChange={(value) => {
              const selected = displayedSettlementSuppliers.find(
                (supplier) => supplier.id === String(value),
              ) ?? null;
              setSettlementSupplier(selected);
              setSameAsSettlement(Boolean(selected && selected.id === fulfillmentSupplier?.id));
            }}
          />
          {settlementSupplierError ? (
            <span className="fulfillment-order-settlement-error">
              {settlementSupplierErrorCode === "SUPPLIER_SCHEMA_NOT_READY"
                ? "供应商主数据未就绪"
                : "结算供应商加载失败"}
              {onRetrySettlementSuppliers ? (
                <Button type="text" size="small" onClick={onRetrySettlementSuppliers}>
                  重试加载
                </Button>
              ) : null}
            </span>
          ) : null}
        </section>
      ) : null}
      onResourceTypeChange={changeResourceType}
      onAdd={addEntries}
    />
  );
}
